// ─────────────────────────────────────────────────────────────────────────────
// Billing guide page
//
// Final page in the guide. Covers:
//   - the in-app Billing page (status displays, callouts, sidebar dot)
//   - the Stripe Customer Portal (where actual changes happen)
//   - the cancellation flow, including the two-step Stripe confirmation
//   - what the yellow indicators mean across all subscription states
//   - failed-payment (past_due) behavior
//   - $69/mo / $599/yr pricing, 30-day trial behavior
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  Callout,
} from "../GuideLayout";

export function GuideBilling() {
  return (
    <GuideLayout
      title="Billing"
      subtitle="Manage your Futures OS Pro subscription, payment method, and billing information."
      prev={{ slug: "profile", label: "Profile Settings" }}
      next={null}
    >
      <H2First>What it's for</H2First>
      <P>
        Billing is the gateway to your subscription. The in-app page
        shows your current status — and, when something needs your
        attention (a queued cancellation, a failed payment), surfaces
        that clearly with a yellow callout and a matching dot on the
        Billing nav item. The actual subscription management —
        updating payment methods, changing billing info, canceling,
        un-canceling — happens in the Stripe Customer Portal, which
        you reach via the <UI>Manage Billing →</UI> button.
      </P>

      <H2>Pricing and trial</H2>
      <P>
        Futures OS Pro is available in two billing options:
      </P>
      <UL>
        <LI><strong>Monthly</strong> — pay each month, cancel anytime</LI>
        <LI><strong>Annual</strong> — pay once per year at a discounted rate</LI>
      </UL>
      <P>
        You choose your billing cadence at sign-up, and you can switch
        between monthly and annual later via the <UI>Manage Billing →</UI>{" "}
        button. Current pricing is displayed at sign-up and in the
        Stripe Customer Portal — that's the source of truth, not this
        page, since prices can change over time.
      </P>
      <P>
        Every new account gets a <strong>30-day free trial</strong>{" "}
        with full access to all features —{" "}
        <strong>no credit card required to start</strong>. Three days
        before your trial ends, an amber banner appears at the top of
        the app prompting you to add a payment method. You can also add
        one anytime from the Billing page via the{" "}
        <UI>Add Payment Method →</UI> button. If no card is added by
        day 30, the trial ends and the account is paused — your data is
        preserved and you can resubscribe anytime.
      </P>

      <Callout kind="info" title="Founder pricing for early customers">
        The first 100 customers get founder pricing — a permanently
        lower monthly and annual rate that stays locked in as long as
        their subscription remains active. If you signed up early
        enough to qualify, your Stripe Customer Portal will show your
        founder rate, and that rate persists for the life of your
        subscription. Cancel and resubscribe later, though, and you'll
        be on whatever pricing is current at that point.
      </Callout>

      <Callout kind="info" title="The trial converts automatically">
        If you've added a payment method by the trial end date, Futures
        OS Pro continues automatically and your card is charged for the
        first billing period. There's no manual "convert to paid" step.
        To prevent the charge, cancel before the trial end date or
        simply don't add a card.
      </Callout>

      <H2>What's included in Pro</H2>
      <P>
        Pro is the only subscription tier — there's no free version of
        Futures OS once your trial ends. Pro includes:
      </P>
      <UL>
        <LI><strong>22 behavioral pattern detectors</strong> — the full suite across Performance, Risk Discipline, Psychology, and Context analytics</LI>
        <LI><strong>AI coaching synthesis</strong> — narrative insights generated from your detector signals (currently planned; see Privacy Policy for status)</LI>
        <LI><strong>Full analytics dashboard</strong> — Performance, Psychology, Context tabs and everything beneath them</LI>
        <LI><strong>Monthly Review</strong> — month-over-month detector-delta narrative</LI>
        <LI><strong>Trade Review</strong> — pattern context flags on each trade</LI>
        <LI><strong>Sparkline trend history</strong> — recent-trades-only signal context throughout the app</LI>
        <LI><strong>Unlimited trades</strong> — no caps on how many you can log</LI>
      </UL>

      <H2>Trial ending soon banner</H2>
      <P>
        Three days before your trial ends, an amber banner appears at
        the top of every page in the app:
      </P>
      <UL>
        <LI>It shows your trial end date and a single <UI>Add Card →</UI> button</LI>
        <LI>Clicking it takes you to the Billing page where you can add a payment method</LI>
        <LI>The banner clears automatically once a card is added and the payment processes</LI>
        <LI>A yellow dot also appears on the Billing nav item so you notice it from any page</LI>
      </UL>
      <P>
        If you choose not to add a card, the banner stays visible until
        the trial ends. After day 30, the app pauses and shows the
        upgrade screen — your data is intact and you can resubscribe
        from that screen at any time.
      </P>

      <H2>Subscription states and what you'll see</H2>
      <P>
        Your Billing page reflects your current subscription state with
        a colored status label and, when relevant, a callout below it.
        These are the states you may see:
      </P>
      <UL>
        <LI>
          <strong>Free Trial</strong> <em style={{ opacity: 0.7 }}>(blue)</em> — your 30-day
          trial is active. A gray <UI>Trial ends [date]</UI> line shows
          the conversion date.
        </LI>
        <LI>
          <strong>Active</strong> <em style={{ opacity: 0.7 }}>(green)</em> — your
          subscription is paid and renewing. A gray <UI>Next billing
          date: [date]</UI> line shows when the next charge happens.
        </LI>
        <LI>
          <strong>Free Trial</strong> or <strong>Active</strong>{" "}
          <em style={{ opacity: 0.7 }}>with a yellow callout</em> — a cancellation is queued.
          You keep access through the date shown, then the subscription
          ends. See the cancellation section below.
        </LI>
        <LI>
          <strong>Past Due</strong> <em style={{ opacity: 0.7 }}>(red)</em> — a payment
          failed. Stripe automatically retries the card over the next 2 weeks.
          <strong> You keep full access during retries</strong> — a red banner
          appears on your Dashboard and the Billing nav gets a yellow dot.
          See the failed-payment section below.
        </LI>
        <LI>
          <strong>Cancelled</strong> <em style={{ opacity: 0.7 }}>(gray)</em> — your
          subscription has ended. You can still log in to view your
          history, but gated features (Analytics, Trade Review, and
          others) are locked until you resubscribe.
        </LI>
      </UL>

      <H2>Canceling your subscription</H2>
      <P>
        Canceling is a multi-step process — partly Stripe's design, and
        partly so you can change your mind at any point along the way.
        Here's what happens end to end:
      </P>
      <UL>
        <LI><strong>Step 1.</strong> Click <UI>Manage Billing →</UI> on the Billing page. You're routed to the Stripe Customer Portal.</LI>
        <LI><strong>Step 2.</strong> Click <UI>Cancel subscription</UI> in the Stripe portal.</LI>
        <LI><strong>Step 3.</strong> Stripe shows a confirmation screen describing what will happen. Click <UI>Cancel subscription</UI> again to confirm. (If you change your mind here, click <UI>Go back</UI>.)</LI>
        <LI><strong>Step 4.</strong> Stripe returns you to the portal home, now showing a <UI>Cancels [date]</UI> badge and a <UI>Don't cancel subscription</UI> button.</LI>
        <LI><strong>Step 5.</strong> Click <UI>← Return to FuturesOS</UI> at the top-left.</LI>
        <LI><strong>Step 6.</strong> Back in Futures OS, the Billing page now shows a yellow callout: <UI>⚠ Cancels on [date] — You'll keep access until this date.</UI> The sidebar Billing item also gets a small yellow dot as a passive reminder.</LI>
      </UL>

      <Callout kind="warn" title="Two-step confirmation in the Stripe portal">
        Stripe's cancel button takes you to a confirmation page, not
        immediate cancellation. You have to click <UI>Cancel
        subscription</UI> a second time to commit. If you only click
        once and then return to Futures OS, nothing has changed — your
        subscription is still active and there will be no yellow
        callout. Same thing on the un-cancel side: <UI>Don't cancel
        subscription</UI> takes you to a <UI>Renew subscription</UI>{" "}
        confirmation page, and you have to click the blue button to
        commit.
      </Callout>

      <H2>Changing your mind: un-canceling</H2>
      <P>
        While your subscription is in the cancellation-pending state
        (yellow callout visible, sidebar dot showing), you can resume
        it at any point before the end date with no interruption:
      </P>
      <UL>
        <LI><strong>Step 1.</strong> Click <UI>Manage Billing →</UI> on the Billing page.</LI>
        <LI><strong>Step 2.</strong> In the Stripe portal, click <UI>Don't cancel subscription</UI>.</LI>
        <LI><strong>Step 3.</strong> Stripe shows a <UI>Renew your subscription</UI> confirmation page. Click the blue <UI>Renew subscription</UI> button to commit.</LI>
        <LI><strong>Step 4.</strong> The portal returns to its normal state — <UI>Cancel subscription</UI> button is back, no <UI>Cancels</UI> badge.</LI>
        <LI><strong>Step 5.</strong> Click <UI>← Return to FuturesOS</UI>.</LI>
        <LI><strong>Step 6.</strong> The yellow callout disappears from Billing and the sidebar dot is removed. Your normal <UI>Trial ends</UI> or <UI>Next billing date</UI> line returns.</LI>
      </UL>
      <P>
        If you cancel and don't un-cancel by the end date, the
        subscription ends and your status flips to <strong>Cancelled</strong>.
        You can resubscribe later — but at whatever pricing is current
        at that point (see the Founder Pricing callout above).
      </P>

      <H2>Cancellation timing</H2>
      <UL>
        <LI><strong>During trial</strong> — canceling stops the automatic conversion to paid. You keep full access until your trial end date, then your account moves to a read-only state. No card is ever charged.</LI>
        <LI><strong>During an active subscription</strong> — canceling stops future renewals. You keep full access through the end of your current billing period (the date shown in the yellow callout). No refund for the current period — you've already paid for it, and you get the full month/year you paid for.</LI>
      </UL>

      <H2>The sidebar yellow dot</H2>
      <P>
        A small yellow dot may appear on the <UI>Billing</UI> item in
        the left sidebar. It's a passive reminder that something on
        your subscription needs your attention — visible from any
        page in the app, so you notice without having to navigate to
        Billing first.
      </P>
      <P>
        The dot appears when <em>either</em> of these is true:
      </P>
      <UL>
        <LI>
          <strong>Cancellation pending</strong> — you've cancelled your
          subscription and access is scheduled to end. The dot says
          "you've got an active cancellation in the system; come back
          here to un-cancel if you change your mind." A yellow banner
          also appears on your Dashboard with the end date and a quick
          link to Billing. The Dashboard banner can be dismissed; the
          dot and Billing callout stay until the cancellation is
          resolved.
        </LI>
        <LI>
          <strong>Past Due (failed payment)</strong> — your last billing
          attempt failed. The dot says "your card needs attention
          before access stops." A more prominent red banner also
          appears at the top of your Dashboard with an Update Payment
          button. Click either to reach Billing, then <UI>Manage Billing →</UI>{" "}
          to update your payment method in Stripe. See the next section
          for what happens during retries.
        </LI>
      </UL>
      <P>
        The dot disappears automatically as soon as the underlying
        condition is resolved — un-cancel, or successfully update your
        payment method, and the indicator clears within a few seconds
        of returning to the app. If it lingers longer than that, a hard
        refresh (Ctrl+Shift+R or Cmd+Shift+R) forces the app to
        re-read your current state.
      </P>

      <H2>What happens if a payment fails</H2>
      <P>
        When Stripe can't successfully charge your card on the renewal
        date — expired card, insufficient funds, fraud hold, anything —
        your subscription moves into the <strong>Past Due</strong>{" "}
        state. Here's what to expect:
      </P>
      <UL>
        <LI><strong>You keep full access during retries.</strong> Past Due is a "fix it soon" state, not a "you're locked out" state. Analytics, Trade Review, the entire app — all stay available while Stripe is working through retries.</LI>
        <LI><strong>You'll see prominent in-app reminders.</strong> A red banner appears at the top of your Dashboard with an Update Payment button. The Billing nav gets a yellow dot. The Billing page itself shows a red Past Due status with the action you need to take.</LI>
        <LI><strong>Stripe retries up to 8 times over 2 weeks.</strong> The exact retry timing is decided by Stripe's Smart Retries — usually starting within hours of the failure and spreading out over the following days. Most card issues resolve on the first or second retry without any user action.</LI>
        <LI><strong>You'll also get email notifications.</strong> Stripe sends payment-failure emails directly to the billing email on file — these come from Stripe, not from Futures OS, so check spam if you don't see them.</LI>
        <LI><strong>To resolve it manually:</strong> click <UI>Manage Billing →</UI>, then <UI>Add payment method</UI> in the Stripe portal. Add the working card and mark it as default. Stripe picks up the new card on the next retry — usually within a few hours.</LI>
        <LI><strong>If your card is actually fine</strong> (e.g., a fraud hold has cleared, the failed charge was a one-off bank glitch): you don't necessarily need to do anything. Stripe will retry on its own schedule and the charge will go through. To force a fresh attempt right away, you can remove the card from the portal and re-add it — Stripe treats it as a new payment method and re-validates.</LI>
        <LI><strong>If all 8 retries fail over the 2-week window:</strong> Stripe automatically cancels the subscription. Status flips to <strong>Cancelled</strong> and you lose access to gated features. Your data is preserved — you can resubscribe with a new payment method to regain access.</LI>
      </UL>

      <Callout kind="warn" title="Don't ignore Past Due">
        Past Due gives you 2 weeks to fix the payment problem, but
        every retry that fails brings you closer to automatic
        cancellation. If you see the banner or get an email, fix it as
        soon as you can — once retries fully fail, restoring access
        requires a fresh subscription, and your founder pricing (if
        you had it) won't carry over.
      </Callout>

      <H2>Manage Billing (Stripe Customer Portal)</H2>
      <P>
        Most of what you'd want to change about your subscription lives
        in the Stripe Customer Portal — a Stripe-hosted page reachable
        via the <UI>Manage Billing →</UI> button. There you can:
      </P>
      <UL>
        <LI><strong>Cancel or un-cancel</strong> — both walked through in detail above</LI>
        <LI><strong>Manage payment methods</strong> — add a new card, mark one as default, or remove an old one</LI>
        <LI><strong>Update billing information</strong> — name, email used for invoices, billing address, and phone number</LI>
        <LI><strong>Download invoices</strong> — scroll down for a list of past payments with downloadable receipts</LI>
      </UL>
      <P>
        The Stripe portal is intentionally separate from Futures OS —
        we don't handle your card data directly. Stripe manages all
        payment information under their own PCI compliance. You'll see
        the URL change to a Stripe domain when you click <UI>Manage Billing →</UI>.
      </P>

      <Callout kind="info" title="Returning to Futures OS">
        At the top-left of the Stripe portal, a <UI>← Return to
        FuturesOS</UI> link sends you back. Changes you made in Stripe
        sync back to Futures OS automatically — usually within a couple
        of seconds. You don't need to refresh; the app polls briefly
        on return so the new state appears on its own.
      </Callout>

      <H2>After cancellation</H2>
      <P>
        Once your access ends, your data is preserved for <strong>150
        days</strong> so you can resubscribe and pick up exactly where
        you left off — same trades, same accounts, same rules, same
        strategies, same eval presets.
      </P>
      <P>
        During those 150 days you can still sign in. Most pages
        (Dashboard, Analytics, Trade Review, Journal, and other gated
        features) will show an upgrade prompt — you'll need to
        resubscribe to access them. The Billing, Profile, Guide, and
        legal pages remain accessible.
      </P>
      <P>
        On day 150, your account is automatically scheduled for
        deletion. You'll see the same "your account is scheduled for
        deletion" screen as the Danger Zone delete flow, with the
        option to restore by resubscribing. You have 30 more days to
        restore.
      </P>
      <P>
        On day 180, your data is <strong>permanently and irreversibly
        deleted</strong>. After that, you'd have to start a fresh
        account from scratch — none of your previous trades or
        settings can be recovered.
      </P>

      <Callout kind="warn" title="Want to keep your data longer? Resubscribe">
        The 180-day window is firm. If you cancel and don't resubscribe
        within 180 days, your data is gone. If you want to preserve
        your history but pause spending, the only option is to
        resubscribe before the window closes — even briefly. Once you
        resubscribe, the deletion clock resets.
      </Callout>

      <Callout kind="warn" title="Account deletion is a separate flow from cancellation">
        Canceling here stops billing and starts the 180-day retention
        clock. If you want to delete your data <em>immediately</em>
        instead, use <UI>Profile → Danger Zone → Delete my account</UI>.
        That action also cancels your subscription as a side effect,
        and gives you a shorter 30-day grace period for restoration
        before permanent deletion. Use the Stripe portal Cancel button
        for "I'm done paying for now" and the Profile Danger Zone for
        "I want my data deleted now."
      </Callout>

      <H2>Questions about billing</H2>
      <P>
        For anything not covered by the Stripe portal — billing
        disputes, plan questions, refund requests for special
        circumstances — email{" "}
        <a href="mailto:admin@futures-os.app">
          admin@futures-os.app
        </a>
        . Response time is usually within a business day.
      </P>
    </GuideLayout>
  );
}



